import { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import {
    downloadDeadlineSchedulePdf,
    downloadMergeOrderPdf,
} from 'components/order-details/main-order-view/document-print-actions/files';
import { DeliveryScheduleDeliveryNoteOrdersTemplate } from 'apollo/templates/delivery-schedule/query.graphql';
import { DeliveryNoteOrder, DeliveryNoteSelection } from 'models/delivery-schedule';

const DELIVERY_NOTE_PDF_TYPE = 'DELIVERY_NOTE_PDF';

const DELIVERY_NOTE_FILE_NAME_KEYS: Record<DeliveryNoteSelection, string> = {
    [DeliveryNoteSelection.SENT_ON_DATE]: 'delivery-schedule:DELIVERY_NOTES_SENT_FILE_NAME',
    [DeliveryNoteSelection.FINISHED_ON_DATE]: 'delivery-schedule:DELIVERY_NOTES_FINISHED_FILE_NAME',
};

export type PrintFeedback = {
    type: 'info' | 'warning' | 'error';
    message: string;
};

type DeliverySchedulePrintOptions = {
    date: Date;
    clinicId?: string;
    departmentId?: string;
};

type DeliveryNoteOrdersResult = {
    deliveryScheduleDeliveryNoteOrders: DeliveryNoteOrder[];
};

// DD-3546: the delivery schedule's Print menu — the day's list, and every delivery note of
// the orders sent or finished on the selected day merged into one PDF. All three honour the
// schedule's clinic and department filters.
export const useDeliverySchedulePrint = ({ date, clinicId, departmentId }: DeliverySchedulePrintOptions) => {
    const { t: translate } = useTranslation(['delivery-schedule', 'orders']);
    const apolloClient = useApolloClient();
    const [printing, setPrinting] = useState(false);
    const [feedback, setFeedback] = useState<PrintFeedback | null>(null);

    // The local calendar day the user picked, not the UTC one.
    const dateLabel = moment(date).format('YYYY-MM-DD');

    const runPrint = async (print: () => Promise<void>) => {
        setFeedback(null);
        setPrinting(true);
        try {
            await print();
        } catch (error) {
            setFeedback({ type: 'error', message: (error as Error).message });
        } finally {
            setPrinting(false);
        }
    };

    const printList = () =>
        runPrint(() => downloadDeadlineSchedulePdf(date, `deadline-${dateLabel}.pdf`, clinicId, departmentId));

    const fetchDeliveryNoteOrders = async (selection: DeliveryNoteSelection): Promise<DeliveryNoteOrder[]> => {
        const { data } = await apolloClient.query<DeliveryNoteOrdersResult>({
            query: DeliveryScheduleDeliveryNoteOrdersTemplate,
            variables: { deliveryNotesInput: { date, clinicId, departmentId, selection } },
            fetchPolicy: 'network-only',
        });
        return data?.deliveryScheduleDeliveryNoteOrders ?? [];
    };

    const buildMissingNotesFeedback = (ordersWithoutNote: DeliveryNoteOrder[]): PrintFeedback => ({
        type: 'warning',
        message: translate('delivery-schedule:ORDERS_WITHOUT_DELIVERY_NOTE', {
            count: ordersWithoutNote.length,
            orderIds: ordersWithoutNote.map((order) => order.orderId).join(', '),
        }),
    });

    const printDeliveryNotes = (selection: DeliveryNoteSelection) =>
        runPrint(async () => {
            const orders = await fetchDeliveryNoteOrders(selection);
            if (!orders.length) {
                setFeedback({ type: 'info', message: translate('delivery-schedule:NO_DELIVERY_NOTES_FOR_DAY') });
                return;
            }
            const ordersWithNote = orders.filter((order) => order.hasDeliveryNotePdf);
            const ordersWithoutNote = orders.filter((order) => !order.hasDeliveryNotePdf);
            if (ordersWithNote.length) {
                const fileName = `${translate(DELIVERY_NOTE_FILE_NAME_KEYS[selection])}-${dateLabel}.pdf`;
                await downloadMergeOrderPdf(
                    ordersWithNote.map((order) => order._id),
                    DELIVERY_NOTE_PDF_TYPE,
                    fileName,
                );
            }
            if (ordersWithoutNote.length) {
                setFeedback(buildMissingNotesFeedback(ordersWithoutNote));
            }
        });

    return { printing, feedback, clearFeedback: () => setFeedback(null), printList, printDeliveryNotes };
};
